import { useRenderNotifier } from '@wui/common-library'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useLocalization } from '@/shared/composables/localization'

import { allowedKeys, PreferenceKey, PREFERENCES_VERSION } from '../constants'

import { applyMigrations } from './migrations'

export type Preferences = { [key in PreferenceKey]?: unknown }
const DEFAULT_PREFERENCES_STATE = { version: PREFERENCES_VERSION } as const

type ImApi = {
  onPreference: (handler: (event: { preferences?: Preferences }) => void | Promise<void>) => void
  setPreference: (preferences: Preferences) => Promise<unknown>
}

let imApiPromise: Promise<ImApi> | null = null

const getImApi = (): Promise<ImApi> => {
  if (!imApiPromise) {
    imApiPromise = import('@wui/im').then(({ useIM }) => useIM() as unknown as ImApi)
  }
  return imApiPromise
}

/** Test-only: drop cached IM API so mocks re-subscribe cleanly between suites. */
export const resetPreferencesImApiCache = () => {
  imApiPromise = null
}

export const usePreferencesStore = defineStore('preferences', () => {
  const { notifyError } = useRenderNotifier()
  const { t } = useLocalization()
  const preferences = ref<Preferences>({ ...DEFAULT_PREFERENCES_STATE })

  const isLoaded = ref(false)

  void getImApi().then((im) => {
    im.onPreference(async (event) => {
      console.debug('Loaded preferences event', event)

      try {
        const rawPreferences = event.preferences ?? {}
        const migrationResult = await applyMigrations(rawPreferences, PREFERENCES_VERSION)
        const currentPreferences = migrationResult.preferences
        const currentVersion = rawPreferences.version

        if (currentVersion !== PREFERENCES_VERSION) {
          console.warn('Version preferences does not match: ', `old-version: ${currentVersion}`, `actual-version: ${PREFERENCES_VERSION}`)
        }

        if (Object.keys(currentPreferences).length > 0 && allowedKeys.some(key => key in currentPreferences)) {
          (Object.keys(currentPreferences) as PreferenceKey[]).forEach((key) => {
          // Библиотека возвращает данные по ключу [productName], если данных нет, то весь объект
          // Ищем значения по ключам в массиве allowedKeys, дополнительных действий не потребовалось.
            preferences.value[key] = currentPreferences[key]
          })
        }

        if (!isLoaded.value) {
          isLoaded.value = true

          const canSyncMigratedPreferencesBack = (currentVersion as number) < PREFERENCES_VERSION
          if (migrationResult.hasChanges && canSyncMigratedPreferencesBack) {
            await im.setPreference(currentPreferences)
          }
        }
      } catch (e) {
        console.error(e)
      }
    })
  }).catch((error: unknown) => {
    console.warn('Failed to subscribe preferences to IM', error)
  })

  const setPreferences = async (key: PreferenceKey, value: unknown) => {
    if(!allowedKeys.includes(key)) {
      console.warn('Preference key not allowed', key)
      return
    }
    const nextPreferences = { ...preferences.value, [key]: value }
    preferences.value = nextPreferences
    try {
      await (await getImApi()).setPreference(nextPreferences)
    } catch (e) {
      console.error(e)
    }
  }

  const clearPreference = async (key: PreferenceKey) => {
    try {
      const newPreferences = { ...preferences.value, [key]: undefined }
      await (await getImApi()).setPreference(newPreferences)
    } catch (e) {
      console.error(e)
    }
  }

  const clearPreferences = async () => {
    try {
      await (await getImApi()).setPreference({ ...DEFAULT_PREFERENCES_STATE })
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
      notifyError(errorMessage)
    }
  }

  const getPreference = (key: PreferenceKey) => {
    return preferences.value[key]
  }

  return {
    preferences,
    clearPreferences,
    clearPreference,
    setPreferences,
    getPreference,
    isLoaded,
  }
})
